import {
  useEffect,
  useState,
} from "react";
import axios from "axios";

import api from "../../../lib/api";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Modal } from "../../../components/ui/Modal";

interface PolizaRenovacion {
  id: number;
  idExterno: string;
  cliente: string;
  tipoPoliza: string;
  fechaVencimiento: string | null;
}

interface CrmRenovarPolizaModalProps {
  poliza: PolizaRenovacion | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError<{ error?: string }>(error)) {
    return (
      error.response?.data?.error ??
      "No se pudo guardar la renovación"
    );
  }

  return "No se pudo guardar la renovación";
}

function formatStoredDate(
  value: string | null
): string {
  if (!value) return "Sin fecha";

  const parts = value.split("-");

  if (parts.length !== 3) return value;

  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function formatDateInput(value: string): string {
  const digits = value
    .replace(/\D/g, "")
    .slice(0, 8);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}/${digits.slice(
    2,
    4
  )}/${digits.slice(4)}`;
}

function parseSpanishDate(
  value: string
): string | null {
  const match =
    /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);

  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  const date = new Date(
    Date.UTC(year, month - 1, day)
  );

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return `${String(year).padStart(
    4,
    "0"
  )}-${String(month).padStart(
    2,
    "0"
  )}-${String(day).padStart(2, "0")}`;
}

export function CrmRenovarPolizaModal({
  poliza,
  onClose,
  onSaved,
}: CrmRenovarPolizaModalProps) {
  const [fechaEfecto, setFechaEfecto] =
    useState("");
  const [
    fechaVencimiento,
    setFechaVencimiento,
  ] = useState("");
  const [primaNeta, setPrimaNeta] =
  useState("");
const [impuestos, setImpuestos] =
  useState("");
const [recargo, setRecargo] =
  useState("");
const [consorcio, setConsorcio] =
  useState("");
const [primaTotal, setPrimaTotal] =
  useState("");
const [
  comisionPorcentaje,
  setComisionPorcentaje,
] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFechaEfecto("");
    setFechaVencimiento("");
    setPrimaNeta("");
setImpuestos("");
setRecargo("");
setConsorcio("");
setPrimaTotal("");
setComisionPorcentaje("");
    setError("");
  }, [poliza]);

  const closeModal = () => {
    if (!saving) onClose();
  };

  const saveRenovacion = async () => {
    if (!poliza) return;

    setError("");

    if (
  poliza.tipoPoliza !== "ACC" &&
  [
    primaNeta,
    impuestos,
    recargo,
    consorcio,
    primaTotal,
    comisionPorcentaje,
  ].some((value) => !value.trim())
) {
  setError(
    "Completa todos los datos financieros."
  );
  return;
}
    const fechaEfectoIso =
      parseSpanishDate(fechaEfecto);

    const fechaVencimientoIso =
      parseSpanishDate(fechaVencimiento);

    if (
      !fechaEfectoIso ||
      !fechaVencimientoIso
    ) {
      setError(
        "Introduce las fechas con el formato DD/MM/AAAA."
      );
      return;
    }

    if (
      fechaVencimientoIso <= fechaEfectoIso
    ) {
      setError(
        "La fecha de vencimiento debe ser posterior a la fecha de efecto."
      );
      return;
    }

    setSaving(true);

    try {
      await api.post(
        `/admin/crm/polizas/${poliza.id}/vigencias`,
        {
          fecha_efecto: fechaEfectoIso,
          fecha_vencimiento:
            fechaVencimientoIso,
        prima_neta:
  poliza.tipoPoliza === "ACC" ? "0" : primaNeta,
impuestos:
  poliza.tipoPoliza === "ACC" ? "0" : impuestos,
recargo:
  poliza.tipoPoliza === "ACC" ? "0" : recargo,
consorcio:
  poliza.tipoPoliza === "ACC" ? "0" : consorcio,
prima_total:
  poliza.tipoPoliza === "ACC" ? "0" : primaTotal,
comision_porcentaje:
  poliza.tipoPoliza === "ACC"
    ? "20"
    : comisionPorcentaje,
          }
      );

      await onSaved();
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={poliza !== null}
      onClose={closeModal}
      title={`Renovar ${poliza?.idExterno ?? "póliza"}`}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={closeModal}
            disabled={saving}
          >
            Cancelar
          </Button>

          <Button
            onClick={saveRenovacion}
            loading={saving}
          >
            Guardar renovación
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          La renovación conservará el ID{" "}
          <strong>{poliza?.idExterno}</strong>. Solo
          añadirá un nuevo periodo de vigencia.
        </div>

        <div className="text-sm text-gray-600">
          <p>
            Cliente:{" "}
            <strong>{poliza?.cliente}</strong>
          </p>

          <p className="mt-1">
            Vencimiento actual:{" "}
            <strong>
              {formatStoredDate(
                poliza?.fechaVencimiento ?? null
              )}
            </strong>
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Nueva fecha de efecto *"
            type="text"
            inputMode="numeric"
            placeholder="DD/MM/AAAA"
            maxLength={10}
            helpText="Ejemplo: 01/11/2027"
            value={fechaEfecto}
            onChange={(event) =>
              setFechaEfecto(
                formatDateInput(event.target.value)
              )
            }
          />

          <Input
            label="Nuevo vencimiento *"
            type="text"
            inputMode="numeric"
            placeholder="DD/MM/AAAA"
            maxLength={10}
            helpText="Ejemplo: 01/11/2028"
            value={fechaVencimiento}
            onChange={(event) =>
              setFechaVencimiento(
                formatDateInput(event.target.value)
              )
            }
          />
       {poliza?.tipoPoliza === "ACC" ? (
  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 md:col-span-2">
    Los importes de esta póliza de Accidentes circuito
    se calcularán automáticamente según los pilotos
    pagados incluidos en la nueva vigencia. La comisión
    será el 20 % de la prima neta.
  </div>
) : (
  <>
    <Input
      label="Prima neta (€) *"
      type="number"
      min="0"
      step="0.01"
      value={primaNeta}
      onChange={(event) =>
        setPrimaNeta(event.target.value)
      }
    />

    <Input
      label="Impuestos (€) *"
      type="number"
      min="0"
      step="0.01"
      value={impuestos}
      onChange={(event) =>
        setImpuestos(event.target.value)
      }
    />

    <Input
      label="Recargo (€) *"
      type="number"
      min="0"
      step="0.01"
      value={recargo}
      onChange={(event) =>
        setRecargo(event.target.value)
      }
    />

    <Input
      label="Consorcio (€) *"
      type="number"
      min="0"
      step="0.01"
      value={consorcio}
      onChange={(event) =>
        setConsorcio(event.target.value)
      }
    />

    <Input
      label="Prima total (€) *"
      type="number"
      min="0"
      step="0.01"
      value={primaTotal}
      onChange={(event) =>
        setPrimaTotal(event.target.value)
      }
    />

    <Input
      label="Comisión sobre prima neta (%) *"
      type="number"
      min="0"
      max="100"
      step="0.01"
      value={comisionPorcentaje}
      onChange={(event) =>
        setComisionPorcentaje(event.target.value)
      }
    />
  </>
)}
        </div>
      </div>
    </Modal>
  );
}